import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar, FileDown, FileText, Loader, AlertCircle, ChevronDown, RefreshCw, Building2,
} from 'lucide-react';
import { employeeAPI, formsAPI, establishmentAPI } from '../api/apiService';
import { useAuth, useActiveEstId } from './AuthContext';
import { getPrimaryRole } from '../utils/permissions';
import './MyPayslip.css';

const MONTHS = [
  { value: 1,  label: 'January'   }, { value: 2,  label: 'February'  },
  { value: 3,  label: 'March'     }, { value: 4,  label: 'April'     },
  { value: 5,  label: 'May'       }, { value: 6,  label: 'June'      },
  { value: 7,  label: 'July'      }, { value: 8,  label: 'August'    },
  { value: 9,  label: 'September' }, { value: 10, label: 'October'   },
  { value: 11, label: 'November'  }, { value: 12, label: 'December'  },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

// Keep only the latest generated payslip per month/year
const dedupePayslips = (forms) => {
  const latest = {};
  forms.filter(f => f.file_name?.toLowerCase().endsWith('.pdf')).forEach(f => {
    const key = `${f.month}-${f.year}`;
    if (!latest[key] || new Date(f.generated_at) > new Date(latest[key].generated_at)) {
      latest[key] = f;
    }
  });
  return Object.values(latest);
};

// ── Employee self-service payslip view ─────────────────────────────
function EmployeePayslipView({ user }) {
  const [profile, setProfile]           = useState(null);
  const [payslips, setPayslips]         = useState([]);
  const [profileLoading, setProfileLoading] = useState(true);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [month, setMonth]               = useState(new Date().getMonth() + 1);
  const [year, setYear]                 = useState(currentYear);

  const fetchPayslips = useCallback(async (employeeId, m, y) => {
    setLoading(true);
    setError('');
    try {
      const response = await formsAPI.listGeneratedForms({ employee_id: employeeId, form_type: 'form_19', month: m, year: y });
      setPayslips(dedupePayslips(response.forms || []));
    } catch {
      setError('Could not load payslips. Please try again.');
      setPayslips([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    employeeAPI.getMe()
      .then(data => { setProfile(data); fetchPayslips(data.employee_id, new Date().getMonth() + 1, currentYear); })
      .catch(err => setError(err.message || 'Could not load profile'))
      .finally(() => setProfileLoading(false));
  }, [fetchPayslips]);

  const monthLabel = (m) => MONTHS.find(x => x.value === parseInt(m))?.label || m;
  const initials = profile
    ? `${profile.first_name?.[0] ?? ''}${profile.last_name?.[0] ?? ''}`.toUpperCase()
    : user?.username?.[0]?.toUpperCase() ?? 'U';

  if (profileLoading) return <div className="mps-center"><Loader size={28} className="mps-spinner" /><p>Loading…</p></div>;

  if (!profile) return (
    <div className="mps-center mps-warn">
      <AlertCircle size={28} />
      <p>No employee record is linked to your account ({user?.email}).<br />Please contact your administrator.</p>
    </div>
  );

  return (
    <div className="mps-page">
      <div className="mps-hero">
        <div className="mps-avatar">{initials}</div>
        <div className="mps-hero-info">
          <h1 className="mps-hero-name">{profile.first_name} {profile.last_name}</h1>
          <div className="mps-hero-meta">
            {profile.position && <span>{profile.position}</span>}
            {profile.department && <><span className="mps-dot">·</span><span>{profile.department}</span></>}
          </div>
          <div className="mps-hero-id">Employee ID: {profile.employee_id}</div>
        </div>
      </div>
      <PayslipFilterAndList
        payslips={payslips} loading={loading} error={error}
        month={month} year={year} monthLabel={monthLabel}
        onMonthChange={setMonth} onYearChange={setYear}
        onLoad={() => fetchPayslips(profile.employee_id, month, year)}
      />
    </div>
  );
}

// ── Admin/Manager payslip view ──────────────────────────────────────
function AdminPayslipView() {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [establishments, setEstablishments] = useState([]);
  const [employees, setEmployees]           = useState([]);
  const [selectedEst, setSelectedEst]       = useState(lockedEstId || '');
  const [selectedEmp, setSelectedEmp]       = useState('');
  const [payslips, setPayslips]             = useState([]);
  const [loading, setLoading]               = useState(false);
  const [estLoading, setEstLoading]         = useState(!lockedEstId);
  const [empLoading, setEmpLoading]         = useState(false);
  const [error, setError]                   = useState('');
  const [month, setMonth]                   = useState(new Date().getMonth() + 1);
  const [year, setYear]                     = useState(currentYear);

  const monthLabel = (m) => MONTHS.find(x => x.value === parseInt(m))?.label || m;

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setSelectedEst(lockedEstId);
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lockedEstId) { setEstLoading(false); return; } // ClientAdmin: use locked est
    establishmentAPI.getAll()
      .then(r => setEstablishments(r.establishments || []))
      .finally(() => setEstLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedEst) { setEmployees([]); setSelectedEmp(''); return; }
    setEmpLoading(true);
    employeeAPI.getByEstablishment(selectedEst)
      .then(r => setEmployees(r.employees || []))
      .catch(() => setEmployees([]))
      .finally(() => setEmpLoading(false));
    setSelectedEmp('');
    setPayslips([]);
  }, [selectedEst]);

  const fetchPayslips = useCallback(async (empId, m, y) => {
    setLoading(true);
    setError('');
    try {
      const response = await formsAPI.listGeneratedForms({ employee_id: empId, form_type: 'form_19', month: m, year: y });
      setPayslips(dedupePayslips(response.forms || []));
    } catch {
      setError('Could not load payslips.');
      setPayslips([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLoad = () => {
    if (selectedEmp) fetchPayslips(selectedEmp, month, year);
  };

  return (
    <div className="mps-page">
      {/* Title */}
      <div className="mps-toolbar">
        <h1 className="mps-page-title">View Payslips</h1>
      </div>

      {/* Selectors */}
      <div className="mps-selector-row">
        {/* Establishment — hidden for ClientAdmin */}
        {!lockedEstId && (
        <div className="mps-selector-card">
          <label className="mps-filter-label">
            <Building2 size={13} /> Organization
          </label>
          <div className="mps-select-wrap">
            <select className="mps-select" value={selectedEst} onChange={e => setSelectedEst(e.target.value)} disabled={estLoading}>
              <option value="">{estLoading ? 'Loading…' : 'Select organization'}</option>
              {establishments.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <ChevronDown size={14} className="mps-select-chev" />
          </div>
        </div>
        )}
        {/* Employee */}
        <div className="mps-selector-card">
          <label className="mps-filter-label">Employee</label>
          <div className="mps-select-wrap">
            <select className="mps-select" value={selectedEmp} onChange={e => { setSelectedEmp(e.target.value); setPayslips([]); }} disabled={!selectedEst || empLoading}>
              <option value="">{empLoading ? 'Loading…' : 'Select employee'}</option>
              {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name} ({e.employee_id})</option>)}
            </select>
            <ChevronDown size={14} className="mps-select-chev" />
          </div>
        </div>
      </div>

      {selectedEmp && (
        <PayslipFilterAndList
          payslips={payslips} loading={loading} error={error}
          month={month} year={year} monthLabel={monthLabel}
          onMonthChange={setMonth} onYearChange={setYear}
          onLoad={handleLoad}
        />
      )}
      {!selectedEmp && selectedEst && (
        <div className="mps-empty" style={{ marginTop: '2rem' }}>
          <FileText size={36} className="mps-empty-icon" />
          <p>Select an employee to view their payslips.</p>
        </div>
      )}
    </div>
  );
}

// ── Shared filter + list panel ──────────────────────────────────────
function PayslipFilterAndList({ payslips, loading, error, month, year, monthLabel, onMonthChange, onYearChange, onLoad }) {
  return (
    <>
      <div className="mps-filter-card">
        <div className="mps-filter-row">
          <div className="mps-filter-group">
            <label className="mps-filter-label"><Calendar size={13} /> Month</label>
            <div className="mps-select-wrap">
              <select className="mps-select" value={month} onChange={e => onMonthChange(parseInt(e.target.value))}>
                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <ChevronDown size={14} className="mps-select-chev" />
            </div>
          </div>
          <div className="mps-filter-group">
            <label className="mps-filter-label"><Calendar size={13} /> Year</label>
            <div className="mps-select-wrap">
              <select className="mps-select" value={year} onChange={e => onYearChange(parseInt(e.target.value))}>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <ChevronDown size={14} className="mps-select-chev" />
            </div>
          </div>
          <button className="mps-btn-load" onClick={onLoad} disabled={loading}>
            {loading ? <Loader size={15} className="mps-spinner-sm" /> : <RefreshCw size={15} />}
            {loading ? 'Loading…' : 'Load Payslips'}
          </button>
        </div>
      </div>

      {error && <div className="mps-error"><AlertCircle size={16} /><span>{error}</span></div>}

      <div className="mps-list-card">
        {loading ? (
          <div className="mps-center"><Loader size={28} className="mps-spinner" /><p>Loading payslips…</p></div>
        ) : payslips.length === 0 ? (
          <div className="mps-empty">
            <FileText size={40} className="mps-empty-icon" />
            <p>No payslips found for {monthLabel(month)} {year}</p>
            <span>Payslips appear here once payroll is processed.</span>
          </div>
        ) : (
          payslips.map((p, i) => (
            <div key={p.id || i} className="mps-row">
              <div className="mps-row-info">
                <FileText size={20} className="mps-row-icon" />
                <div>
                  <div className="mps-row-title">{monthLabel(p.month || month)} {p.year || year} — Payslip</div>
                  <div className="mps-row-sub">Generated: {p.generated_at ? new Date(p.generated_at).toLocaleDateString('en-IN') : '—'}</div>
                </div>
              </div>
              <button className="mps-btn-dl" onClick={() => p.s3_url && window.open(p.s3_url, '_blank')} disabled={!p.s3_url}>
                <FileDown size={15} /> Download
              </button>
            </div>
          ))
        )}
      </div>
    </>
  );
}

// ── Main export ─────────────────────────────────────────────────────
export default function MyPayslip() {
  const { user } = useAuth();
  const role = getPrimaryRole(user);
  if (role === 'SuperAdmin' || role === 'ClientAdmin' || role === 'MultiClientAdmin') return <AdminPayslipView />;
  return <EmployeePayslipView user={user} />;
}
